import type { ExpressionStudy } from "./expression-studies";
import type { Reading } from "./types";
import { computeReadingMetrics } from "./metrics";
import type { WordCommandManifest } from "../app/word-command-prompts";

const WORD_PATTERN = /\p{L}+(?:[’'-]\p{L}+)*/gu;
const EXPRESSION_STANCE_IDS = ["direct", "tactful", "warm-authority", "restrained", "punchy"] as const;
const EXPRESSION_SIZE_COUNTS = { quick: 4, standard: 5, deep: 3 } as const;

export function normalizeContentWord(value: string) {
  return value.toLocaleLowerCase().replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "");
}

export function validateReading(reading: Reading, options: { requirePublished?: boolean } = {}) {
  const requirePublished = options.requirePublished ?? true;
  const errors: string[] = [];
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(reading.id)) errors.push("id must be a stable slug");
  if (!/^\d{4}-\d{2}-\d{2}\.\d+$/.test(reading.edition)) errors.push("edition must be date.sequence");
  if (reading.taxonomyVersion !== "taxonomy_v2") errors.push("taxonomy version is unsupported");
  if (reading.provenance !== "Verbalore original") errors.push("provenance must be explicit");
  if (requirePublished && reading.editorialState !== "published") errors.push("only editorially published works belong in the public catalog");
  if (!requirePublished && !["approved", "in_review", "published"].includes(reading.editorialState)) {
    errors.push("finished editorial work must be in review, approved, or published");
  }
  const receiptGates = new Set(reading.receipts.map((receipt) => receipt.gate));
  for (const gate of ["G0", "G1", "G2", "G3", "G4", "G5"] as const) {
    if (!receiptGates.has(gate)) errors.push(`missing ${gate} editorial receipt`);
  }
  if (reading.receipts.some((receipt) => receipt.edition !== reading.edition || !receipt.artifact.trim())) {
    errors.push("editorial receipts must bind to this edition and a maintained artifact");
  }
  if (reading.segments.length < 8) errors.push("reading needs at least eight maintained segments");
  if (JSON.stringify(reading).includes("—")) errors.push("em dashes are not allowed in authored reading content");

  const segmentIds = new Set<string>();
  const corpus = reading.segments.map((segment) => {
    if (segmentIds.has(segment.id)) errors.push(`duplicate segment id ${segment.id}`);
    segmentIds.add(segment.id);
    return segment.text;
  }).join(" ");
  if (reading.chapters?.length) {
    if (reading.chapters.length < 3) errors.push("chaptered reading needs at least three meaningful chapters");
    const chapterIds = new Set<string>();
    const openings = new Set<string>();
    reading.chapters.forEach((chapter, index) => {
      if (chapterIds.has(chapter.id)) errors.push(`duplicate chapter id ${chapter.id}`);
      if (openings.has(chapter.openingSegmentId)) errors.push(`duplicate chapter opening ${chapter.openingSegmentId}`);
      if (!segmentIds.has(chapter.openingSegmentId)) errors.push(`${chapter.id}: opening segment does not exist`);
      if (chapter.number !== index + 1) errors.push(`${chapter.id}: chapter number must follow reading order`);
      chapterIds.add(chapter.id);
      openings.add(chapter.openingSegmentId);
    });
    if (computeReadingMetrics(reading).estimatedMinutes < 25) errors.push("chaptered long work must offer at least 25 honest minutes with exploration");
  }
  const corpusWords = new Set((corpus.match(WORD_PATTERN) || []).map(normalizeContentWord));

  for (const [key, entry] of Object.entries(reading.words)) {
    if (normalizeContentWord(entry.word) !== key) errors.push(`${key}: key and word disagree`);
    if (!corpusWords.has(key)) errors.push(`${key}: word does not occur in reading`);
    if (!corpus.includes(entry.sentence)) errors.push(`${key}: source sentence is not exact`);
    if (entry.sentence && corpus.split(entry.sentence).length !== 2) errors.push(`${key}: source sentence must identify exactly one maintained context`);
    if (!entry.translations.English || !entry.translations.Hindi || !entry.translations.Punjabi) {
      errors.push(`${key}: all maintained translations are required`);
    }
  }

  for (const word of reading.exercise.targetWords) {
    if (!reading.words[normalizeContentWord(word)]) errors.push(`exercise target ${word} has no lexical entry`);
  }
  if (!reading.taxonomy.outcomes.length || !reading.taxonomy.rhetoricalMoves.length) {
    errors.push("taxonomy needs outcomes and rhetorical moves");
  }
  if (!reading.taxonomy.mood.length || !reading.taxonomy.topics.length || !reading.taxonomy.sensitivityFlags.length) {
    errors.push("taxonomy v2 needs mood, topics, and sensitivity flags");
  }
  const metrics = computeReadingMetrics(reading);
  if (metrics.wordCount < 1 || metrics.estimatedMinutes < 1) errors.push("computed reading metrics are invalid");
  return errors;
}

export function assertValidCatalog(readings: Reading[]) {
  const ids = new Set<string>();
  for (const reading of readings) {
    if (ids.has(reading.id)) throw new Error(`Duplicate reading id: ${reading.id}`);
    ids.add(reading.id);
    const errors = validateReading(reading);
    if (errors.length) throw new Error(`${reading.id}: ${errors.join("; ")}`);
  }
  return readings;
}

function hasText(value: string) {
  return value.trim().length > 0;
}

function expressionText(study: ExpressionStudy) {
  return [
    study.title,
    ...study.craft,
    study.context,
    study.skill,
    study.situation,
    study.tangledDraft,
    study.intention,
    study.invariant,
    study.concept,
    study.practiceCue,
    ...study.stances.flatMap((stance) => [
      stance.label,
      stance.line,
      stance.landing,
      stance.protects,
      stance.tradeoff,
    ]),
  ].join(" ");
}

export function assertValidExpressionStudies(studies: ExpressionStudy[]) {
  const errors: string[] = [];
  const ids = new Set<string>();
  const numbers = new Set<string>();
  const sizeCounts: Record<ExpressionStudy["size"], number> = { quick: 0, standard: 0, deep: 0 };

  if (studies.length !== 12) errors.push(`expected 12 studies, received ${studies.length}`);

  for (const study of studies) {
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(study.id)) errors.push(`${study.id}: id must be a stable slug`);
    if (ids.has(study.id)) errors.push(`${study.id}: duplicate id`);
    if (numbers.has(study.number)) errors.push(`${study.id}: duplicate study number ${study.number}`);
    ids.add(study.id);
    numbers.add(study.number);
    sizeCounts[study.size] += 1;

    if (study.provenance !== "Original Verbalore") errors.push(`${study.id}: provenance must be Original Verbalore`);
    if (!/^\d{2}$/.test(study.number)) errors.push(`${study.id}: number must use two digits`);
    if (study.craft.length < 2 || study.craft.some((term) => !hasText(term))) errors.push(`${study.id}: craft terms are incomplete`);
    if (![
      study.title,
      study.context,
      study.skill,
      study.situation,
      study.tangledDraft,
      study.intention,
      study.invariant,
      study.concept,
      study.practiceCue,
    ].every(hasText)) errors.push(`${study.id}: authored study fields are incomplete`);
    if (study.minutes < 1 || study.minutes > 15) errors.push(`${study.id}: minutes must remain within the MVP study range`);
    if (study.stances.length !== EXPRESSION_STANCE_IDS.length) errors.push(`${study.id}: expected five stances`);

    const stanceIds = study.stances.map((stance) => stance.id);
    if (new Set(stanceIds).size !== stanceIds.length) errors.push(`${study.id}: stance ids must be unique`);
    for (const stanceId of EXPRESSION_STANCE_IDS) {
      if (!stanceIds.includes(stanceId)) errors.push(`${study.id}: missing ${stanceId} stance`);
    }
    for (const stance of study.stances) {
      if (![stance.label, stance.line, stance.landing, stance.protects, stance.tradeoff].every(hasText)) {
        errors.push(`${study.id}/${stance.id}: articulation is incomplete`);
      }
    }

    if (expressionText(study).includes("—")) errors.push(`${study.id}: em dashes are not allowed in authored expression content`);
  }

  for (const [size, expected] of Object.entries(EXPRESSION_SIZE_COUNTS)) {
    const actual = sizeCounts[size as ExpressionStudy["size"]];
    if (actual !== expected) errors.push(`expected ${expected} ${size} studies, received ${actual}`);
  }

  if (errors.length) throw new Error(`Expression Studio content invalid: ${errors.join("; ")}`);
  return studies;
}

export function assertValidWordCommandManifest(
  readings: Reading[],
  manifest: WordCommandManifest,
) {
  const errors: string[] = [];
  const corpus = new Map<string, string>();
  for (const reading of readings) {
    for (const word of Object.keys(reading.words)) {
      if (corpus.has(word)) errors.push(`${word}: contextual review word occurs in more than one reading`);
      corpus.set(word, reading.id);
    }
  }
  if (manifest.schemaVersion !== "vocabulary-review-v2") errors.push("unsupported manifest schema version");
  if (manifest.policyVersion !== "word-command-v2") errors.push("unsupported evidence policy version");
  if (manifest.reviewCorpus !== "contextual-words-v1") errors.push("review corpus must be contextual-words-v1");

  const packWords = Object.keys(manifest.packs);
  const missing = [...corpus.keys()].filter((word) => !manifest.packs[word]);
  const extra = packWords.filter((word) => !corpus.has(word));
  if (missing.length) errors.push(`missing prompt packs: ${missing.join(", ")}`);
  if (extra.length) errors.push(`prompt packs outside declared corpus: ${extra.join(", ")}`);

  const promptIds = new Set<string>();
  for (const [word, pack] of Object.entries(manifest.packs)) {
    if (pack.word !== word) errors.push(`${word}: pack key and word disagree`);
    const readingId = corpus.get(word);
    if (readingId && pack.senseId !== `${readingId}:${word}:v1`) errors.push(`${word}: senseId does not bind the maintained reading sense`);
    if (pack.checks.length < 3) errors.push(`${word}: at least three closed checks are required`);
    const forms = new Set(pack.checks.map((check) => check.assessmentForm));
    if (!forms.has("context_cloze") || !forms.has("sense_discrimination")) {
      errors.push(`${word}: checks need distinct context and sense-assessment forms`);
    }
    const normalizedStems = new Set<string>();
    for (const check of pack.checks) {
      if (!/^[a-z0-9]+\.(?:recall|contrast)\.(?:primary|delayed)\.v\d+$/.test(check.id)) {
        errors.push(`${word}: invalid immutable check id ${check.id}`);
      }
      if (promptIds.has(check.id)) errors.push(`${word}: duplicate prompt id ${check.id}`);
      promptIds.add(check.id);
      const normalizedStem = check.stem.toLocaleLowerCase().replace(/\W+/g, " ").trim();
      if (normalizedStems.has(normalizedStem)) errors.push(`${word}: duplicate closed-check wording`);
      normalizedStems.add(normalizedStem);
      if (check.options.length !== 3 || new Set(check.options).size !== 3) errors.push(`${word}/${check.id}: exactly three unique options are required`);
      if (!check.options.includes(check.answer)) errors.push(`${word}/${check.id}: answer must occur in options`);
    }
    for (const prompt of [pack.use, pack.natural]) {
      if (!/\.v\d+$/.test(prompt.id)) errors.push(`${word}: prompt id needs an immutable revision`);
      if (promptIds.has(prompt.id)) errors.push(`${word}: duplicate prompt id ${prompt.id}`);
      promptIds.add(prompt.id);
    }
    if (pack.use.criteria.length !== 2 || new Set(pack.use.criteria.map((criterion) => criterion.id)).size !== 2) {
      errors.push(`${word}: authored use needs exactly two distinct self-checks`);
    }
    if (JSON.stringify(pack).includes("—")) errors.push(`${word}: em dashes are not allowed in review content`);
    if (/\b(mastered|mastery|guaranteed learned)\b/i.test(JSON.stringify(pack))) {
      errors.push(`${word}: prompt content makes a false learning claim`);
    }
  }

  if (packWords.length !== corpus.size) errors.push(`expected ${corpus.size} prompt packs, received ${packWords.length}`);
  if (errors.length) throw new Error(`Vocabulary review manifest invalid: ${errors.join("; ")}`);
  return manifest;
}
